/**
 * Axis arithmetic for the charts: clean tick values (0 / 50 / 100, never
 * 0 / 47 / 94) and a linear scale from data to pixels.
 */

/** A "nice" step for roughly `count` intervals across [0, max]: 1, 2 or 5 times a power of ten. */
export function niceStep(max: number, count: number): number {
  if (max <= 0) return 1;
  const raw = max / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / power;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return Math.max(1, nice * power);
}

/** Ticks from 0 to a nice ceiling at or above max. Always includes 0. */
export function niceTicks(max: number, count = 4): number[] {
  const step = niceStep(max, count);
  const top = Math.max(step, Math.ceil(max / step) * step);
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v));
  return ticks;
}

export function linear(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  return (v: number) => r0 + ((v - d0) / span) * (r1 - r0);
}

/** Which x-axis labels to show so they never collide: about one per `minGap` pixels, always the last. */
export function labelIndices(count: number, width: number, minGap = 64): number[] {
  if (count === 0) return [];
  const every = Math.max(1, Math.ceil(count / Math.max(1, Math.floor(width / minGap))));
  const out: number[] = [];
  for (let i = 0; i < count; i += every) out.push(i);
  const last = count - 1;
  if (out[out.length - 1] !== last) {
    // The last day is always labelled. It is less than one step from the
    // previous label, so that one gives way rather than collide with it.
    if (out.length > 1) out.pop();
    out.push(last);
  }
  return out;
}
